import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { AnimatedLogo } from './AnimatedLogo';
import { BrandLogoStrip } from './BrandLogoStrip';
import { ModelStrip } from './ModelStrip';
import { QuickScoringButton } from '../credit/QuickScoringButton';
import { useStore } from '../../contexts/StoreContext';
const ease = [0.23, 1, 0.32, 1] as const;
const speedLines = [{
  top: '18%',
  width: '22vw',
  delay: '0s'
}, {
  top: '42%',
  width: '14vw',
  delay: '1.2s'
}, {
  top: '63%',
  width: '28vw',
  delay: '2.1s'
}, {
  top: '81%',
  width: '18vw',
  delay: '0.6s'
}];
export function Hero() {
  const {
    brands,
    vehicles
  } = useStore();
  const count = vehicles.filter(v => v.published).length;
  return <>
      <ModelStrip data-id="element-712" data-mp="1v4n7uqjhrz3y" />
      <section className="relative overflow-hidden bg-navy" data-id="element-713" data-mp="1v33r6ytxbm1n">
        {/* Líneas de velocidad neón */}
        <div aria-hidden className="pointer-events-none absolute inset-0" data-id="element-714" data-mp="1q6lnm2abxtqw">
          {speedLines.map((l, i) => <span key={i} className="speed-line absolute left-0 h-px bg-neon-hi/70" style={{
          top: l.top,
          width: l.width,
          animationDelay: l.delay,
          boxShadow: '0 0 8px #1463FF'
        }} data-id="element-715" data-mp="1mv1769byc783" data-mpi={i} />)}
        </div>
        <div aria-hidden className="absolute inset-x-0 bottom-0 h-px bg-silver-dark/40" data-id="element-716" data-mp="1czzmbpoplrbb" />

        <div className="container-x relative grid items-center gap-8 pb-10 pt-10 lg:grid-cols-[1fr_1fr] lg:gap-6 lg:pb-14 lg:pt-14" data-id="element-717" data-mp="1hxhuveyte4is">
          <div className="order-2 lg:order-1" data-id="element-718" data-mp="2alx5zqets2ou">
            <motion.p initial={{
            opacity: 0,
            x: -12
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.3,
            ease
          }} className="inline-flex items-center gap-2 rounded-full border border-silver-dark/40 bg-ink/50 px-3 py-1 text-xs font-semibold tracking-wider text-silver" data-id="element-719" data-mp="2akltn27bdmm4">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-neon-hi" aria-hidden data-id="element-720" data-mp="2tcekapywuvqo" />
              {brands.length} MARCAS · {count} VERSIONES 0 KM
            </motion.p>
            <h1 className="mt-4 font-display text-[2.6rem] font-black leading-[0.95] tracking-tight sm:text-6xl xl:text-7xl" data-id="element-721" data-mp="336ap6wrergyp">
              {['TODAS', 'LAS', 'MARCAS.'].map((w, i) => <motion.span key={w} className="text-metal mr-[0.22em] inline-block" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.3,
              ease,
              delay: 0.06 * i
            }} data-id="element-722" data-mp="2u5vs70530dfd" data-mpi={i}>
                  {w}
                </motion.span>)}
              <br data-id="element-723" data-mp="2akixkdbh4u3s" />
              <motion.span className="text-glow inline-block text-neon" initial={{
              opacity: 0,
              y: 20,
              filter: 'blur(6px)'
            }} animate={{
              opacity: 1,
              y: 0,
              filter: 'blur(0px)'
            }} transition={{
              duration: 0.3,
              ease,
              delay: 0.25
            }} data-id="element-724" data-mp="2ajzgeytne9qt">
                UN SOLO LUGAR.
              </motion.span>
            </h1>
            <motion.p initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            ease,
            delay: 0.35
          }} className="mt-5 max-w-lg text-base text-mist sm:text-lg" data-id="element-725" data-mp="c14e28ybslv3">
              Descubrí tu próximo 0 km, compará alternativas y encontrá la financiación adecuada para vos.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            ease,
            delay: 0.45
          }} className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap" data-id="element-726" data-mp="3phveb4tbezfr">
              <Link to="/catalogo" className="btn btn-metal group h-12 px-6" data-id="element-727" data-mp="73cmjg1lf1rf">
                EXPLORAR 0 KM
                <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 group-hover:translate-x-1" aria-hidden data-id="element-728" data-mp="36r45q5h5jd2f" />
              </Link>
              <Link to="/simulador" className="btn btn-metal h-12 px-6" data-id="element-729" data-mp="3aq39vnv275st">
                SIMULAR MI FINANCIACIÓN
              </Link>
              <Link to="/chequeo" className="btn btn-metal h-12 px-6" data-id="element-730" data-mp="2mogbn10rps0b">
                CHEQUEO CREDITICIO
              </Link>
              <QuickScoringButton data-id="element-731" data-mp="31e5se4lcg4lp" />
            </motion.div>
          </div>
          <div className="order-1 lg:order-2" data-id="element-732" data-mp="2wmnq9d7l3xz5">
            <AnimatedLogo data-id="element-733" data-mp="2wkt6i281ubwn" />
          </div>
        </div>

        <BrandLogoStrip data-id="element-734" data-mp="3ad9b37glmhzo" />
      </section>
    </>;
}